import { formatDistanceToNowStrict } from 'date-fns';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

import { ErrorState } from '@/components/fleet/states';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useFleetTask } from '@/hooks/use-fleet-task';
import { cn } from '@/lib/utils';
import type { TaskStatus } from '@/types/fleet';

const STATUS_META: Record<
  TaskStatus,
  { label: string; className: string; spinning: boolean }
> = {
  pending: { label: 'Pending', className: 'bg-slate-100 text-slate-700', spinning: true },
  running: { label: 'Running', className: 'bg-blue-100 text-blue-800', spinning: true },
  success: { label: 'Succeeded', className: 'bg-emerald-100 text-emerald-800', spinning: false },
  failure: { label: 'Failed', className: 'bg-red-100 text-red-800', spinning: false },
  stopped: { label: 'Stopped', className: 'bg-amber-100 text-amber-900', spinning: false },
};

/**
 * Task-status surface for a Fleet *lifecycle* task (decommission / quarantine
 * / recover). These hit the dedicated Fleet endpoints and are polled via
 * `GET /api/admin/fleet/tasks/:id`, which returns the `FleetTask` shape — so
 * this is a distinct component from the provisioning `TaskPanel`.
 */
export function FleetTaskPanel({ taskId }: { taskId: number }): JSX.Element {
  const taskQuery = useFleetTask(taskId);

  if (taskQuery.isLoading) {
    return (
      <div className="space-y-2" aria-label="Loading task" role="status">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (taskQuery.isError || !taskQuery.data) {
    return (
      <ErrorState
        title={`Couldn't load task #${taskId}.`}
        error={taskQuery.error}
        onRetry={() => void taskQuery.refetch()}
      />
    );
  }

  const task = taskQuery.data;
  const meta = STATUS_META[task.status];
  const StatusIcon =
    task.status === 'success'
      ? CheckCircle2
      : task.status === 'failure'
        ? XCircle
        : Loader2;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={cn('border-0', meta.className)}>
          <StatusIcon
            className={cn('h-3.5 w-3.5', meta.spinning && 'animate-spin')}
            aria-hidden
          />
          {meta.label}
        </Badge>
      </div>

      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Task</dt>
        <dd className="font-mono text-xs">#{task.task_id}</dd>
        {task.started_at && (
          <>
            <dt className="text-muted-foreground">Started</dt>
            <dd>{formatDistanceToNowStrict(new Date(task.started_at))} ago</dd>
          </>
        )}
        {task.finished_at && (
          <>
            <dt className="text-muted-foreground">Finished</dt>
            <dd>{formatDistanceToNowStrict(new Date(task.finished_at))} ago</dd>
          </>
        )}
      </dl>

      {task.status === 'failure' && task.output_url && (
        <a
          href={task.output_url}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-primary hover:underline"
        >
          View logs
        </a>
      )}

      {meta.spinning && (
        <p className="text-xs text-muted-foreground">
          Polling the Fleet API every 2s — this can take several minutes.
        </p>
      )}
    </div>
  );
}
